/**
 * EVENTSFLO-ADMIN-BACKUP-UI-COMPONENT-SPLIT-V1
 *
 * Archive list table + row dropdown + pagination. Pure presentational —
 * V1 markup, classNames and data-testid values preserved 1:1.
 */
import React from "react";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../../components/ui/dropdown-menu";
import { Download, Eye, MoreVertical, Trash2 } from "lucide-react";
import { formatBytes, formatDate, PAGE_SIZE } from "./backupFormatters";

export default function BackupArchiveTable({
  items,
  total,
  offset,
  loading,
  onPageChange,
  onInspect,
  onDownload,
  onDelete,
}) {
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-base">Archives ({total})</CardTitle>
        <div className="flex items-center gap-2 text-xs text-zinc-400">
          <span>
            Page {currentPage} / {pageCount}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(Math.max(0, offset - PAGE_SIZE))}
            disabled={offset === 0}
            data-testid="prev-page-btn"
          >
            Prev
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(offset + PAGE_SIZE)}
            disabled={offset + PAGE_SIZE >= total}
            data-testid="next-page-btn"
          >
            Next
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-sm text-zinc-400">Loading…</div>
        ) : items.length === 0 ? (
          <div className="text-sm text-zinc-400 py-8 text-center" data-testid="empty-state">
            No backups yet. Click <strong>Create backup</strong> to generate one.
          </div>
        ) : (
          <Table data-testid="backup-list-table">
            <TableHeader>
              <TableRow>
                <TableHead>Filename</TableHead>
                <TableHead>Size</TableHead>
                <TableHead>Generated</TableHead>
                <TableHead>Expires</TableHead>
                <TableHead>By</TableHead>
                <TableHead>State</TableHead>
                <TableHead>Docs</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((b) => (
                <TableRow key={b.backup_id} data-testid={`backup-row-${b.backup_id}`}>
                  <TableCell className="font-mono text-xs">{b.filename}</TableCell>
                  <TableCell>{formatBytes(b.size_bytes)}</TableCell>
                  <TableCell>{formatDate(b.generated_at)}</TableCell>
                  <TableCell>{formatDate(b.expires_at)}</TableCell>
                  <TableCell className="text-xs">{b.generated_by_email || "—"}</TableCell>
                  <TableCell>
                    {b.exists_on_disk ? (
                      <Badge className="bg-green-500/20 text-green-400 border-green-500/30">on disk</Badge>
                    ) : (
                      <Badge className="bg-red-500/20 text-red-400 border-red-500/30">missing</Badge>
                    )}
                    {b.is_expired && (
                      <Badge className="ml-1 bg-amber-500/20 text-amber-400 border-amber-500/30">expired</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-xs">
                    {(b.counts?.documents_written ?? 0).toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          data-testid={`row-actions-${b.backup_id}`}
                        >
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() => onInspect(b)}
                          data-testid={`inspect-${b.backup_id}`}
                        >
                          <Eye className="w-4 h-4 mr-2" /> Inspect
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => onDownload(b)}
                          disabled={!b.exists_on_disk}
                          data-testid={`download-${b.backup_id}`}
                        >
                          <Download className="w-4 h-4 mr-2" /> Download
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="text-red-400"
                          onClick={() => onDelete(b)}
                          data-testid={`delete-${b.backup_id}`}
                        >
                          <Trash2 className="w-4 h-4 mr-2" /> Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
